import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type WorkspaceRole = "OWNER" | "ADMIN" | "VIEWER";

export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  archivedAt: string | null;
  createdAt: string;
}

export interface WorkspaceWithProjects {
  id: string;
  slug: string;
  name: string;
  role: WorkspaceRole;
  archivedAt: string | null;
  projects: ProjectSummary[];
}

export function useMyWorkspaces(opts: { includeArchived?: boolean } = {}) {
  const includeArchived = opts.includeArchived ?? false;
  return useQuery<{ workspaces: WorkspaceWithProjects[] }>({
    queryKey: ["me", "workspaces", { includeArchived }],
    queryFn: () =>
      api<{ workspaces: WorkspaceWithProjects[] }>(
        `/api/me/workspaces${includeArchived ? "?includeArchived=true" : ""}`,
      ),
    staleTime: 60_000,
  });
}

interface CreateWorkspaceResult {
  workspaceId: string;
  workspaceSlug: string;
  projectId: string;
  projectSlug: string;
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; projectName?: string }) =>
      api<CreateWorkspaceResult>("/api/me/workspaces", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

interface CreateProjectResult {
  projectId: string;
  projectSlug: string;
  name: string;
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; name: string }) =>
      api<CreateProjectResult>(
        `/api/workspaces/${input.workspaceId}/projects`,
        {
          method: "POST",
          body: JSON.stringify({ name: input.name }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
      // The new project becomes the active scope, so per-project caches
      // are stale across the board.
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["calls"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["projectSettings"] });
    },
  });
}

export function useArchiveWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      api(`/api/workspaces/${workspaceId}/archive`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useRestoreWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      api(`/api/me/workspaces/${workspaceId}/restore`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; projectId: string }) =>
      api(
        `/api/workspaces/${input.workspaceId}/projects/${input.projectId}/archive`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useRestoreProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; projectId: string }) =>
      api(
        `/api/workspaces/${input.workspaceId}/projects/${input.projectId}/restore`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export interface WorkspaceMember {
  userId: string;
  role: WorkspaceRole;
  addedAt: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}

export function useWorkspaceMembers(workspaceId: string | null | undefined) {
  return useQuery<{ members: WorkspaceMember[] }>({
    queryKey: ["workspace", workspaceId, "members"],
    queryFn: () =>
      api<{ members: WorkspaceMember[] }>(
        `/api/workspaces/${workspaceId}/members`,
      ),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
}

export function useRenameWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; name: string }) =>
      api<{ id: string; slug: string; name: string }>(
        `/api/workspaces/${input.workspaceId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ name: input.name }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useChangeMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workspaceId: string;
      userId: string;
      role: WorkspaceRole;
    }) =>
      api<{ userId: string; role: WorkspaceRole }>(
        `/api/workspaces/${input.workspaceId}/members/${input.userId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ role: input.role }),
        },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workspace", vars.workspaceId, "members"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: WorkspaceRole;
  token: string;
  createdAt: string;
  expiresAt: string;
  invitedById: string;
}

export function useInvitations(workspaceId: string | null | undefined) {
  return useQuery<{ invitations: PendingInvitation[] }>({
    queryKey: ["workspace", workspaceId, "invitations"],
    queryFn: () =>
      api<{ invitations: PendingInvitation[] }>(
        `/api/workspaces/${workspaceId}/invitations`,
      ),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workspaceId: string;
      email: string;
      role: "ADMIN" | "VIEWER";
    }) =>
      api<{
        invitationId: string;
        token: string;
        link: string;
        emailSent: boolean;
      }>(`/api/workspaces/${input.workspaceId}/invitations`, {
        method: "POST",
        body: JSON.stringify({ email: input.email, role: input.role }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["workspace", vars.workspaceId, "invitations"],
      });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; invitationId: string }) =>
      api(
        `/api/workspaces/${input.workspaceId}/invitations/${input.invitationId}`,
        { method: "DELETE" },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["workspace", vars.workspaceId, "invitations"],
      });
    },
  });
}

export interface InvitationPreview {
  workspaceName: string;
  workspaceSlug: string;
  inviterName: string;
  inviterEmail: string | null;
  invitedEmail: string;
  role: WorkspaceRole;
  expiresAt: string;
}

export function useInvitationPreview(token: string | undefined) {
  return useQuery<InvitationPreview>({
    queryKey: ["invitation", token, "preview"],
    queryFn: () => api<InvitationPreview>(`/api/invitations/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{
        workspaceId: string;
        projectId: string | null;
        role?: WorkspaceRole;
        alreadyMember?: boolean;
      }>(`/api/invitations/${token}/accept`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workspaceId: string; userId: string }) =>
      api(
        `/api/workspaces/${input.workspaceId}/members/${input.userId}`,
        { method: "DELETE" },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["workspace", vars.workspaceId, "members"] });
      qc.invalidateQueries({ queryKey: ["me", "workspaces"] });
    },
  });
}

export function useSetActiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api<{ lastActiveProjectId: string; lastActiveWorkspaceId: string }>(
        "/api/me/active-project",
        {
          method: "POST",
          body: JSON.stringify({ projectId }),
        },
      ),
    onSuccess: () => {
      // Active project changed — every per-project read is now stale. Drop
      // them all rather than try to invalidate selectively; safest path
      // until the frontend cuts over to nested URLs (which would auto-scope
      // each query key by project).
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["calls"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["scheduledCalls"] });
    },
  });
}
